import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Grid } from '@material-ui/core';
import { Formik, Form, yupToFormErrors, validateYupSchema } from 'formik';
import { Helmet } from 'react-helmet';
import { parse } from 'query-string';

// CONSTANTS
import { siteUrl } from '@/constants/common';
import {
  ROUTE_DISCOVER,
  ROUTE_CHANNELS,
  ROUTE_START_NOW,
  ROUTE_FIND_MY_BUNDLE,
  ROUTE_RESULTS,
  ROUTE_CHANNELS_WITH_ID,
  ROUTE_START,
  ROUTE_VIDEO_MIGRATION,
  ROUTE_PARTNER_VIDEO_MIGRATION,
  ROUTE_PARTNER_START,
} from '@/constants/routes';
import { startInitFormData, wizardSteps, wizardStepsNewOrder } from '@/constants/wizard';
import { LOCATIONS_START_PAGE } from '@/constants/locations';

// UTILITY
import {
  setYupValidationSchema,
  validateFieldFromSchema,
} from '@/utility/validationSchema/validationSchema';
import {
  analyticsPageLoaded,
  analyticsLoadedQuestion,
  analyticsAnswerSubmitted,
  analyticsAnswerClicked,
  analyticsQuestionSkipped,
  analyticsInternalLinks,
} from '@/utility/analytics';
import { getLink, pagePartnerProtector, routeEnhance } from '@/utility/routes';
import { sendParentMessage, storageAvailable, getSearchParams } from '@/utility/common';
import bugsnag from '@/utility/bugsnagClient';
import store from '@/utility/store';
import { getDomainData } from '@/utility/partner';
import { FMBChangeFlowVariant } from '@/utility/split';
import { getS3ImageUrl } from '@/utility/content';

// STORE
import { AppActions, WizardActions, CountiesActions, ChannelsActions } from '@/store/actions';

// HOC
import Layout from '@/hoc/Layout';
import Guard from '@/hoc/Guard';
import withPrefetch from '@/hoc/withPrefetch';
import withRouter from '@/hoc/withRouter';

// COMPONENTS
import HeaderWizard from '@/components/HeaderWizard';
import FooterWizard from '@/components/FooterWizard';
import Loader from '@/components/Loader';
import DialogAlert from '@/components/Dialogs/DialogAlert';
import DialogExitPopup from '@/components/Dialogs/DialogExitPopup';
import Question from '@/components/Question';

// HELP
import Household from './help/Household';
import ZipCode from './help/ZipCode';
import DVR from './help/DVR';
import AlreadyHave from './help/AlreadyHave';
import Streaming from './help/Streaming';
import Content from './help/Content';
import Sports from './help/Sports';
import DeviceIncluded from './help/DeviceIncluded';

// STYLES
import { GridContainer, ScrollableArea, Container } from './styles';
import Stepper from '@/components/Stepper/Stepper';

const defaultOGImg = getS3ImageUrl('/og-image/new/fmb-og-1.png');
const { originPath } = getDomainData();
const isStorageAvailable = storageAvailable();

const sendAnswersAnalytics = (stepname, values) => {
  if (stepname === 'already-have' || stepname === 'streaming' || stepname === 'content') {
    const answers = values[stepname] || [];
    const answersString = answers.join();

    analyticsAnswerSubmitted(
      {
        target: {
          type: 'checkbox',
          dataset: { text: answersString },
          name: stepname,
        },
      },
      { flow: 'fmb' },
    );
  }
};

class StartContent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      activeStep: 1,
      steps: [],
      showCountyStep: false,
      skippedAddressStep: false,
      showDeviceContractStep: false,
      preventNextStep: false,
      curTimerId: null,
      unique_id: '',
      initialFormData: startInitFormData,
      address: null,
      isShortFlow: props.router.routerMatch({ path: ROUTE_FIND_MY_BUNDLE }),
    };
    this.form = React.createRef();
    this.params = getSearchParams(document.location.search);
  }

  componentDidMount() {
    const { isShortFlow } = this.state;
    const {
      isPartnerLink,
      router: { routerMatch, params },
      extraQuestions,
      getSkipQuestions,
      // getPartner,
      setPartner,
      getUniqueId,
      isPartnerDomain,
      partnerData,
      handlePrefetch,
    } = this.props;
    const isVideoMigration =
      routerMatch({ path: ROUTE_VIDEO_MIGRATION }) ||
      routerMatch({ path: ROUTE_PARTNER_VIDEO_MIGRATION });
    const paramsData = parse(this.params.toString(), { arrayFormat: 'bracket' });
    const hasParams = !!Object.keys(paramsData).length;
    const hasPrev = this.params ? this.params.has('prev') : false;
    const startData = store.get('start-data') || null;
    const skipQuestions = this.getSkipQuestionsList();

    const steps = this.getListOfSteps(skipQuestions, extraQuestions, paramsData);
    const activeStep = hasPrev ? steps.length : 1;
    const form_version = isShortFlow ? 'short' : 'long';
    let slug = '';
    let flowParam = this.params.get('flow') || '';

    if (isVideoMigration) flowParam = 'video-migration';

    if (hasParams || flowParam) {
      if (flowParam) paramsData.flow = flowParam;

      this.setParamsInitData(paramsData);
    }

    if (isPartnerLink) {
      slug = routerMatch({ path: ROUTE_START_NOW }) ? 'start-now' : params.slug || params.partner;

      if (isPartnerDomain) {
        slug = partnerData.slug;
      }

      if (slug) {
        setPartner(slug);
      }
    }

    getSkipQuestions(slug || 'mybundle', { flow: flowParam });

    if (startData) {
      this.setState(
        prevState => ({
          activeStep,
          unique_id: startData.unique_id,
          initialFormData: {
            ...prevState.initialFormData,
            ...startData,
            partner_id_fk: isPartnerLink ? startData.partner_id_fk : 0,
            agree_device_contract: null,
          },
        }),
        () => {
          if (hasParams || flowParam) {
            if (flowParam) paramsData.flow = flowParam;

            this.setParamsInitData(paramsData);
          }
        },
      );

      if (!startData.unique_id) {
        getUniqueId(slug, form_version);
      }
    } else {
      getUniqueId(slug, form_version);
    }

    this.setState({ steps });

    document.addEventListener('keydown', this.handleKeyDown, { passive: true });

    if (!isStorageAvailable) {
      bugsnag.notify(new Error('Local storage is unavailable'));
    }

    // prefetch next page
    handlePrefetch(ROUTE_CHANNELS);

    window.prerendercloudReady = false;
  }

  componentDidUpdate(prevProps, prevState) {
    const { steps, activeStep, initialFormData, address, skippedAddressStep } = this.state;
    const {
      router: { routerMatch },
      counties,
      skipQuestions,
      extraQuestions,
      skipQuestionsLoading,
      partner_id,
      unique_id,
      internet_option,
      countiesZipCode,
      partner,
      partnerData,
      isPartnerDomain,
      getSkipQuestions,
      setPartner,
    } = this.props;
    const { setFieldValue } = this.form.current;
    const hasPartnerData = !!Object.keys(partnerData).length;
    const isVideoMigration =
      routerMatch({ path: ROUTE_VIDEO_MIGRATION }) ||
      routerMatch({ path: ROUTE_PARTNER_VIDEO_MIGRATION });

    let flowParam = this.params.get('flow') || '';

    if (isVideoMigration) flowParam = 'video-migration';

    pagePartnerProtector(partner, ['use_fmb'], ROUTE_DISCOVER);

    if (prevState.activeStep !== activeStep) {
      this.form.current.validateForm();
    }

    if (
      isPartnerDomain &&
      !!Object.keys(prevProps.partnerData).length !== hasPartnerData &&
      hasPartnerData
    ) {
      getSkipQuestions(partnerData.slug, { flow: flowParam });
      setPartner(partnerData.slug);
    }

    if (
      prevProps.skipQuestionsLoading !== skipQuestionsLoading &&
      prevProps.partner_id !== partner_id &&
      !skipQuestionsLoading
    ) {
      const paramsData = parse(this.params.toString(), { arrayFormat: 'bracket' });
      const hasPrev = this.params ? this.params.has('prev') : false;
      let updatedSteps = [...steps];
      let updatedInitialState = {};

      if (flowParam) paramsData.flow = flowParam;

      if (
        (prevProps.skipQuestions.length !== skipQuestions.length && skipQuestions.length) ||
        (prevProps.extraQuestions.length !== extraQuestions.length && extraQuestions.length)
      ) {
        updatedSteps = this.getListOfSteps(skipQuestions, extraQuestions, paramsData);

        extraQuestions.forEach(question => {
          let value = '';

          if (question.type === 'checkbox') {
            value = [];
          }
          updatedInitialState = { ...updatedInitialState, [question.slug]: value };
        });
      }

      const curActiveStep = hasPrev ? updatedSteps.length : 1;
      const stepName = this.getStepName(curActiveStep, updatedSteps);
      const partner_extra = this.getStepIsExtra(curActiveStep, updatedSteps);
      const properties = {
        question_slug: stepName,
        partner_extra,
        partner: partner.slug,
        partner_id,
        flow: 'fmb',
      };

      this.setState(
        {
          steps: updatedSteps,
          activeStep: curActiveStep,
          initialFormData: {
            ...updatedInitialState,
            ...initialFormData,
            partner_id_fk: partner_id,
          },
        },
        () => {
          if (Object.keys(paramsData).length) {
            this.setParamsInitData(paramsData);
          }
        },
      );

      analyticsLoadedQuestion(properties);
    }

    if (prevProps.unique_id !== unique_id && unique_id) {
      this.setState({ unique_id });
    }

    if (
      (prevProps.counties !== counties && counties) ||
      (prevState.skippedAddressStep !== skippedAddressStep && counties && !internet_option)
    ) {
      if (internet_option === false) {
        if (skippedAddressStep || (address && countiesZipCode)) {
          if (counties.length > 1) {
            const properties = {
              question_slug: 'county',
              partner_extra: false,
              partner: partner.slug,
              partner_id,
            };

            this.setState({ showCountyStep: true });

            analyticsLoadedQuestion(properties);
          } else if (counties.length === 1) {
            setFieldValue('dma_id', counties[0].dma_id_fk);
            setFieldValue('county_id_fk', counties[0].county_id.toString());

            setTimeout(() => {
              this.handleNextStep();
            }, 0);
          } else {
            this.handleNextStep();
          }
        } else {
          const properties = {
            question_slug: 'address',
            partner_extra: false,
            partner: partner.slug,
            partner_id,
          };

          analyticsLoadedQuestion(properties);
        }

        if (prevProps.countiesZipCode !== countiesZipCode && countiesZipCode) {
          setFieldValue('zipcode', countiesZipCode);
        }
      } else if (internet_option === true) {
        if (counties.length > 1) {
          const properties = {
            question_slug: 'county',
            partner_extra: false,
            partner: partner.slug,
            partner_id,
            flow: 'fmb',
          };

          this.setState({ showCountyStep: true });

          analyticsLoadedQuestion(properties);
        } else if (counties.length === 1) {
          setFieldValue('dma_id', counties[0].dma_id_fk);
          setFieldValue('county_id_fk', counties[0].county_id.toString());

          setTimeout(() => {
            this.handleNextStep();
          }, 0);
        } else {
          this.handleNextStep();
        }
      }
    }
  }

  componentWillUnmount() {
    const { resetSkipQuestions, cleanCountiesState } = this.props;

    resetSkipQuestions();
    cleanCountiesState();

    document.removeEventListener('keydown', this.handleKeyDown);
  }

  getSkipQuestionsList = () => {
    const { isShortFlow } = this.state;
    const { skipQuestions } = this.props;

    const shortFlowSkipQuestions = [
      'device-included',
      'already-have',
      'streaming',
      'content',
      'sports',
    ];

    return isShortFlow ? shortFlowSkipQuestions : skipQuestions;
  };

  isValidField = (filed, value) => {
    const { t, extraQuestions, requiredQuestions } = this.props;

    const validationSchema = setYupValidationSchema('startCSRSchema', t, null, {
      extraQuestions: [...extraQuestions],
      requiredQuestions,
    });

    return validateFieldFromSchema(filed, value, validationSchema);
  };

  getListOfSteps = (skipQuestions, extraQuestions, paramsData = {}) => {
    const { isPartnerLink, flowVariant } = this.props;
    const stepsList =
      flowVariant === FMBChangeFlowVariant.Variant.newOrder && !isPartnerLink
        ? wizardStepsNewOrder
        : wizardSteps;

    let steps = [];

    stepsList.forEach(step => {
      if (!skipQuestions.includes(step.name)) steps.push(step);
    });

    if (extraQuestions.length) {
      const updatedQuestions = extraQuestions.map(question => ({
        name: question.slug,
        partner_extra: true,
      }));
      steps = [...steps, ...updatedQuestions];
    }

    if (Object.keys(paramsData).length) {
      if (paramsData.zipcode) paramsData.zip = paramsData.zipcode;

      steps = steps.filter(step => {
        let stepName = step.name;

        if (stepName === 'zip') stepName = 'zipcode';

        return !(
          step.name in paramsData &&
          paramsData[step.name] &&
          this.isValidField(stepName, paramsData[step.name])
        );
      });
    }

    return steps;
  };

  setParamsInitData = params => {
    if (!params) return;

    const { initialFormData } = this.state;
    const prefilledFormData = { ...initialFormData };

    for (const [key, value] of Object.entries(prefilledFormData)) {
      if (
        params[key] &&
        typeof value === typeof params[key] &&
        this.isValidField(key, params[key])
      ) {
        prefilledFormData[key] = params[key];

        if (key === 'zipcode') {
          prefilledFormData.dma_id = '';
          prefilledFormData.county_id_fk = '';
        }
      }

      if (key === 'zipcode' && params.confirm_zip) {
        prefilledFormData[key] = params.confirm_zip;
      }
    }

    this.setState(prevState => ({
      initialFormData: {
        ...prevState.initialFormData,
        ...prefilledFormData,
      },
    }));
  };

  getStepName = (activeStep, steps) => {
    return steps[activeStep - 1]?.name || '';
  };

  getStepIsExtra = (activeStep, steps) => {
    return steps[activeStep - 1]?.partner_extra || false;
  };

  getStepValue = (stepname, values) => {
    const { showCountyStep } = this.state;

    if (stepname === 'device-included') {
      return values.device_included;
    }

    if (stepname === 'zip') {
      if (showCountyStep) {
        return {
          dma_id: values.dma_id,
          county_id_fk: values.county_id_fk,
        };
      }

      return values.zipcode;
    }

    return values[stepname];
  };

  setPreventNextStep = status => {
    this.setState({ preventNextStep: status });
  };

  handleNextStep = () => {
    if (!this.form.current) {
      return;
    }

    const {
      steps,
      activeStep,
      unique_id,
      address,
      skippedAddressStep,
      showCountyStep,
    } = this.state;
    const {
      isPartnerLink,
      getCounties,
      getCountiesByAddress,
      counties,
      router: { navigate, location },
      skipQuestions,
      partner,
      isIframe,
      iframeType,
      extraQuestions,
      partner_id,
      internet_option,
      countiesZipCode,
      asyncInternetOptions,
      submitForm,
    } = this.props;
    const { values, setFieldValue } = this.form?.current;
    const stepName = this.getStepName(activeStep, steps);
    const skipZip = skipQuestions.includes('zip');
    const answers = [];
    const isZipStep = stepName === 'zip';
    const isZipLogic =
      (isZipStep && values.zipcode && counties === null) ||
      (isZipStep &&
        values.zipcode &&
        internet_option === false &&
        !skippedAddressStep &&
        countiesZipCode === null);
    // const isDeviceIncludedStep = stepName === 'device-included';
    // const showDeviceContract = isDeviceIncludedStep && values.device_included === 'yes';
    const showDeviceContract = false;
    const stepValue = this.getStepValue(stepName, values);
    const hasValue = !!(typeof stepValue === 'number' ? stepValue : stepValue?.length);

    // Analytics logic
    if (isZipLogic) {
      if (internet_option === false && !address) {
        const properties = {
          question_slug: 'address',
          partner: partner.slug,
          partner_id,
          flow: 'fmb',
        };

        analyticsQuestionSkipped(properties);
      }
    } else if (showCountyStep) {
      if (!stepValue.dma_id && !values.county_id_fk) {
        const properties = {
          question_slug: 'county',
          partner: partner.slug,
          partner_id,
          flow: 'fmb',
        };
        analyticsQuestionSkipped(properties);
      }
    } else if (showDeviceContract && values.agree_device_contract === '') {
      const properties = {
        question_slug: 'agree_device_contract',
        partner: partner.slug,
        partner_id,
        flow: 'fmb',
      };

      analyticsQuestionSkipped(properties);
    } else if (!hasValue) {
      const properties = {
        question_slug: stepName,
        partner: partner.slug,
        partner_id,
        flow: 'fmb',
      };
      analyticsQuestionSkipped(properties);
    }

    if (extraQuestions.length) {
      const curQuestion = extraQuestions.find(item => item.slug === stepName);

      extraQuestions.forEach(question => {
        if (question.slug in values) {
          const answer = values[question.slug].length > 0 ? values[question.slug] : null;

          answers.push({ id: question.id, answer });
        }
      });

      if (curQuestion && curQuestion.type === 'text' && values[curQuestion.slug].length > 0) {
        analyticsAnswerSubmitted(
          {
            target: {
              type: curQuestion.type,
              dataset: { text: values[curQuestion.slug] },
              name: curQuestion.slug,
            },
          },
          { flow: 'fmb' },
        );
      }
    }

    const search = location?.search || '';
    const prevPath = search ? location.pathname + search : location.pathname;

    const startData = {
      ...values,
      partner_id_fk: partner_id,
      answers,
      questionsCount: steps.length,
      skipZip,
      prevPath,
      partner,
      isPartnerLink,
      unique_id,
    };

    store.set('start-data', startData);

    if (
      activeStep === steps.length &&
      !(isZipLogic || (showDeviceContract && values.agree_device_contract === null))
    ) {
      const isSmallIframeType = iframeType === 'small';
      const channelsRoute = isSmallIframeType
        ? getLink(ROUTE_CHANNELS_WITH_ID, { unique_id })
        : ROUTE_CHANNELS;

      const callback = () => {
        const routeEnhanced = routeEnhance(channelsRoute, {
          state: { location: LOCATIONS_START_PAGE },
          withoutIframe: isSmallIframeType,
        });

        analyticsInternalLinks({
          unique_id: routeEnhanced.state.unique_id,
          from_page_path: routeEnhanced.state.from_page_path,
          from_search_params: routeEnhanced.state.from_search_params,
          to_page_path: routeEnhanced.pathname,
          to_search_params: routeEnhanced.search,
          partner_id: routeEnhanced.state.partner_id,
          location: routeEnhanced.state.location,
        });

        if (isSmallIframeType) {
          window.open(
            `${originPath}${routeEnhanced.pathname}?${routeEnhanced.search}&prefilled`,
            '_blank',
            'noopener,noreferrer',
          );
          window.location.reload(true);
        } else {
          navigate(routeEnhanced);
        }
      };

      if (isSmallIframeType) {
        submitForm({ ...startData, isSmallIframeType }, callback);
      } else {
        callback();
      }

      return;
    }

    if (isZipLogic) {
      const partnerId = partner_id || 0;

      if (counties === null && internet_option === null) {
        getCounties(values.zipcode, partnerId, unique_id, LOCATIONS_START_PAGE);

        // Send zipcode analytics
        if (values.zipcode) {
          analyticsAnswerSubmitted(
            {
              target: {
                type: 'zipcode',
                dataset: { text: values.zipcode },
                name: 'zipcode',
              },
            },
            { flow: 'fmb' },
          );
        }
      } else if (internet_option === false && !address) {
        this.setState({ skippedAddressStep: true });
        asyncInternetOptions(unique_id);
      } else if (internet_option === false && address && countiesZipCode === null) {
        getCountiesByAddress(address, unique_id, values.zipcode);
      }
    } else if (showDeviceContract && values.agree_device_contract === null) {
      // this.setState({ showDeviceContractStep: true });
      this.setState({ showDeviceContractStep: false });
      setFieldValue('agree_device_contract', '');
    } else {
      const nextStepName = this.getStepName(activeStep + 1, steps);
      const partner_extra = this.getStepIsExtra(activeStep + 1, steps);

      const properties = {
        question_slug: nextStepName,
        partner_extra,
        partner: partner.slug,
        partner_id,
        flow: 'fmb',
      };

      sendAnswersAnalytics(stepName, values);

      if (showCountyStep) {
        this.setState(prevState => ({
          activeStep: prevState.activeStep + 1,
          showCountyStep: false,
        }));
      } else {
        this.setState(prevState => ({ activeStep: prevState.activeStep + 1 }));
      }

      analyticsLoadedQuestion(properties);
    }

    if (isIframe) {
      const isMetronet = partner?.partner_slug === 'metronet';

      sendParentMessage({ goToTop: true, scrollTarget: isMetronet ? 'top' : 'iframe' });
    }
  };

  handleBackStep = () => {
    if (!this.form.current) {
      return;
    }

    const { values, setFieldValue } = this.form.current;
    const { steps, activeStep } = this.state;
    const { cleanCountiesState, isIframe, partner, partner_id } = this.props;
    const activeStepName = this.getStepName(activeStep, steps);
    const prevStepName = this.getStepName(activeStep - 1, steps);
    const prevPartnerExtra = this.getStepIsExtra(activeStep - 1, steps);

    const properties = {
      question_slug: prevStepName,
      partner_extra: prevPartnerExtra,
      partner: partner.slug,
      partner_id,
      flow: 'fmb',
    };

    if (values.zipcode && (prevStepName === 'zip' || activeStepName === 'zip')) {
      setFieldValue('dma_id', '');
      setFieldValue('county_id_fk', '');
      cleanCountiesState();
      this.setState({ showCountyStep: false, skippedAddressStep: false, address: null });
    }

    if (prevStepName === 'device-included' || activeStepName === 'device-included') {
      setFieldValue('agree_device_contract', null);
      this.setState({ showDeviceContractStep: false });
    }

    this.setState(prevState => ({ activeStep: prevState.activeStep - 1 }));

    analyticsLoadedQuestion(properties);

    if (isIframe) {
      const isMetronet = partner?.partner_slug === 'metronet';

      sendParentMessage({ goToTop: true, scrollTarget: isMetronet ? 'top' : 'iframe' });
    }
  };

  handleSelectAddress = address => {
    this.setState({ address });
  };

  handleSelectRadioCard = (event, handleChange) => {
    if (!this.form.current) {
      return;
    }

    const { curTimerId } = this.state;
    const { setFieldValue } = this.form.current;
    const dma_id = event.target?.dataset?.dma_id_fk || '';

    if (dma_id) {
      setFieldValue('dma_id', dma_id);
    }

    handleChange(event);

    analyticsAnswerClicked(event, { flow: 'fmb' });

    analyticsAnswerSubmitted(event, { flow: 'fmb' });

    this.setPreventNextStep(true);

    if (curTimerId) {
      clearTimeout(curTimerId);
      this.setState({ curTimerId: null });
    }

    const timerId = setTimeout(() => {
      this.handleNextStep();
      setTimeout(() => {
        this.setPreventNextStep(false);
      }, 400);
    }, 500);

    this.setState({ curTimerId: timerId });
  };

  handleSelectCheckbox = (event, handleChange) => {
    handleChange(event);

    analyticsAnswerClicked(event, { flow: 'fmb' });
  };

  handleKeyDown = event => {
    if (!this.form.current) {
      return;
    }

    const { preventNextStep, address } = this.state;
    const { isValid } = this.form.current;

    if (event.keyCode === 13 && event.srcElement) {
      const { activeStep, steps } = this.state;
      const elClassName = event.srcElement?.className;
      const isAutocomplete = elClassName.includes('MuiAutocomplete');
      const stepName = this.getStepName(activeStep, steps);
      const isZipStep = stepName === 'zip';

      if (isAutocomplete && address && isZipStep) {
        this.handleNextStep();
        return;
      }

      if (isAutocomplete) return;
    }

    if (event.keyCode === 13 && isValid && !preventNextStep) {
      this.handleNextStep();
    }
  };

  handleSubmit = (values, { setSubmitting }) => {
    setSubmitting(false);
  };

  handleValidate = (values, validationSchema) => {
    const { requiredQuestions, skipQuestions } = this.props;

    try {
      validateYupSchema(values, validationSchema, true, {
        requiredQuestions,
        skipQuestions,
      });
    } catch (err) {
      return yupToFormErrors(err);
    }

    return {};
  };

  isRequiredQuestion = slug => {
    const { requiredQuestions } = this.props;

    return requiredQuestions.includes(slug);
  };

  handleChangeHelmetData = () => {
    if (this.lastPath !== window.location.pathname) {
      analyticsPageLoaded();
      this.lastPath = window.location.pathname;
    }

    window.prerendercloudReady = true;
  };

  render() {
    const {
      steps,
      activeStep,
      showCountyStep,
      initialFormData,
      skippedAddressStep,
      preventNextStep,
      showDeviceContractStep,
    } = this.state;
    const {
      isPartnerLink,
      t,
      isIframe,
      iframeType,
      counties,
      countiesLoading,
      skipQuestions,
      skipQuestionsLoading,
      partnerLoading,
      partner,
      partner_id,
      extraQuestions,
      requiredQuestions,
      internet_option,
      isPartnerDomain,
      partnerData,
      partnerSettings,
      unique_id,
      submitLoading,
    } = this.props;
    const isPartner = !!partner.slug;
    const stepName = this.getStepName(activeStep, steps);
    const validationSchema = setYupValidationSchema('startSchema', t, stepName, {
      extraQuestions: [...extraQuestions],
      requiredQuestions,
    });
    const stepsCount = steps.length;
    const skipEmailQuestion = skipQuestions.includes('email');
    const questionData = extraQuestions.find(question => question.slug === stepName);
    const isSmallIframeType = iframeType === 'small';

    let containerClasses = 'wizard-form fmbpage';

    if (isPartner) {
      containerClasses += ` partner-${partner.slug} formpage`;
    }

    const partnerLength = Object.keys(partner).length;
    const hasPartnerData = isPartnerLink
      ? !!partnerLength && partner_id !== '' && !(skipQuestionsLoading || partnerLoading)
      : true;

    const seoData = {
      canonicalUrl: `${siteUrl}${ROUTE_START}`,
      ogUrl: isPartner
        ? `${siteUrl}${getLink(ROUTE_PARTNER_START, { partner: partner.slug })}`
        : `${siteUrl}${ROUTE_START}`,
      title: partner?.meta?.title || '',
      description: partner?.meta?.description || '',
      image: partner?.meta?.image || defaultOGImg,
    };

    return (
      <Layout>
        {!partnerLoading && (
          <Helmet onChangeClientState={this.handleChangeHelmetData} titleTemplate="%s | MyBundle">
            <meta property="og:site_name" content="MyBundle" />
            <link rel="canonical" href={seoData.canonicalUrl} />
            <meta property="og:url" content={seoData.ogUrl} />
            <meta property="og:type" content="website" />
            <meta property="fb:app_id" content="608514030003288" />
            <meta property="og:image" content={seoData.image} />
            <meta name="robots" content="index, follow, max-image-preview:large" />
            <meta
              name="twitter:title"
              content={seoData.title || 'MyBundle - Personalized Streaming TV Advice'}
            />
            <meta name="twitter:image" content={seoData.image} />
            <meta name="twitter:site" content="@mybundletv" />
            <meta name="twitter:creator" content="@mybundletv" />
            <meta name="twitter:card" content="summary" />
            {!!seoData.title && <title>{seoData.title}</title>}
            {!!seoData.title && <meta property="og:title" content={seoData.title} />}
            {!!seoData.description && (
              <meta property="og:description" content={seoData.description} />
            )}
            {!!seoData.description && <meta name="description" content={seoData.description} />}
            {!!seoData.description && (
              <meta name="twitter:description" content={seoData.description} />
            )}
          </Helmet>
        )}
        <Formik
          innerRef={this.form}
          initialValues={initialFormData}
          validate={values => this.handleValidate(values, validationSchema)}
          onSubmit={this.handleSubmit}
          enableReinitialize
          validateOnMount
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isValid,
            setFieldValue,
            setFieldTouched,
          }) => (
            <div className={containerClasses}>
              <HeaderWizard
                type="fmb"
                questionsCount={stepsCount}
                questionStepperProps={{ active: true, current: activeStep }}
                channelQuestionsCount={skipEmailQuestion ? 1 : 2}
                showPrev={activeStep > 1}
                showNext={activeStep <= stepsCount}
                onNextPress={this.handleNextStep}
                onPrevPress={this.handleBackStep}
                prevBtnProps={{ disabled: !isValid || countiesLoading }}
                nextBtnProps={{ disabled: !isValid || countiesLoading || preventNextStep }}
                partner={partnerData || partner}
                loading={skipQuestionsLoading || partnerLoading}
                isPartnerDomain={isPartnerDomain}
                location={LOCATIONS_START_PAGE}
              />
              {isIframe && !!partnerSettings.use_fmb_iframe_progress && (
                <Stepper
                  className="stepper--iframe-progress"
                  count={stepsCount + 2}
                  active
                  current={activeStep}
                />
              )}
              <ScrollableArea>
                <Container className="page-content">
                  <GridContainer container justifyContent="center" alignItems="center">
                    <Grid item xs={12} md={8}>
                      <Form onSubmit={handleSubmit}>
                        {stepName === 'household' && (
                          <Household
                            values={values}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            partner_data={partner || {}}
                            onRadioCardChange={this.handleSelectRadioCard}
                            isRequired={this.isRequiredQuestion('household')}
                          />
                        )}
                        {stepName === 'zip' && (
                          <ZipCode
                            values={values}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            setFieldValue={setFieldValue}
                            setFieldTouched={setFieldTouched}
                            errors={errors}
                            touched={touched}
                            counties={counties}
                            showCountyStep={showCountyStep}
                            skippedAddressStep={skippedAddressStep}
                            internetOption={internet_option}
                            onRadioCardChange={this.handleSelectRadioCard}
                            onAddressSelect={this.handleSelectAddress}
                            partner={partner || {}}
                            analyticsLocation={LOCATIONS_START_PAGE}
                            isRequired={this.isRequiredQuestion('zipcode')}
                          />
                        )}
                        {stepName === 'dvr' && (
                          <DVR
                            values={values}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            onRadioCardChange={this.handleSelectRadioCard}
                            partner_data={partner || {}}
                            isRequired={this.isRequiredQuestion('dvr')}
                          />
                        )}
                        {stepName === 'device-included' && (
                          <DeviceIncluded
                            values={values}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            showDeviceContractStep={showDeviceContractStep}
                            onRadioCardChange={this.handleSelectRadioCard}
                            isRequired={this.isRequiredQuestion('device-included')}
                          />
                        )}
                        {stepName === 'already-have' && (
                          <AlreadyHave
                            values={values}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            onCheckboxChange={this.handleSelectCheckbox}
                            partner_data={partner || {}}
                            isRequired={this.isRequiredQuestion('already-have')}
                          />
                        )}
                        {stepName === 'streaming' && (
                          <Streaming
                            values={values}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            onCheckboxChange={this.handleSelectCheckbox}
                            isRequired={this.isRequiredQuestion('streaming')}
                          />
                        )}
                        {stepName === 'content' && (
                          <Content
                            values={values}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            errors={errors}
                            touched={touched}
                            setFieldValue={setFieldValue}
                            onCheckboxChange={this.handleSelectCheckbox}
                            isRequired={this.isRequiredQuestion('content')}
                          />
                        )}
                        {stepName === 'sports' && (
                          <Sports
                            values={values}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            onRadioCardChange={this.handleSelectRadioCard}
                            isRequired={this.isRequiredQuestion('sports')}
                          />
                        )}
                        {!!questionData && (
                          <Question
                            data={questionData}
                            values={values}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            errors={errors}
                            touched={touched}
                            onRadioCardChange={this.handleSelectRadioCard}
                            onCheckboxChange={this.handleSelectCheckbox}
                            isRequired={this.isRequiredQuestion(questionData.slug)}
                          />
                        )}
                      </Form>
                    </Grid>
                  </GridContainer>
                </Container>
              </ScrollableArea>
              <FooterWizard
                type="fmb"
                showPrev={activeStep > 1}
                showNext={isSmallIframeType ? activeStep < stepsCount : activeStep <= stepsCount}
                onNextPress={this.handleNextStep}
                onPrevPress={this.handleBackStep}
                prevBtnProps={{ disabled: countiesLoading || submitLoading }}
                nextBtnProps={{ disabled: !isValid || countiesLoading || preventNextStep }}
                showSubmitBtn={isSmallIframeType && activeStep === stepsCount}
                onSubmit={this.handleNextStep}
                submitBtnProps={{ btntext: t('buttons.selectChannels'), disabled: submitLoading }}
                partner={partner}
                hiddenProps={{
                  smDown: false,
                }}
              />
            </div>
          )}
        </Formik>
        {!isStorageAvailable && hasPartnerData && (
          <DialogAlert
            open
            text={t('alerts.storageUnavailable')}
            partner={partner}
            partner_id={partner_id}
          />
        )}
        <Guard iframe={false} partner={{ use_fmb_leave_modal: true }}>
          <DialogExitPopup
            title={t('exitPopupDialog.title')}
            subtitle={t('exitPopupDialog.subtitle')}
            buttonConfirmTitle={t('buttons.exploreMarketplace')}
            maxWidth="sm"
            allowedRoutes={[ROUTE_CHANNELS, ROUTE_RESULTS]}
            confirmRoute={ROUTE_DISCOVER}
            partnerName={partner?.partner_name}
            analyticsId={unique_id}
          />
        </Guard>
        <Loader active={countiesLoading} text={t('channels.loaders.questions')} />
        <Loader
          active={skipQuestionsLoading || partnerLoading}
          fullFilled
          text={partnerLoading ? t('partner.loaders.default') : t('channels.loaders.questions')}
        />
      </Layout>
    );
  }
}

StartContent.defaultProps = {
  flowVariant: FMBChangeFlowVariant.Variant.regular,
};

StartContent.propTypes = {
  flowVariant: PropTypes.string,
  isPartnerLink: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
  partner: PropTypes.object.isRequired,
  partner_id: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  unique_id: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  skipQuestions: PropTypes.array.isRequired,
  extraQuestions: PropTypes.array.isRequired,
  requiredQuestions: PropTypes.array.isRequired,
  skipQuestionsLoading: PropTypes.bool.isRequired,
  partnerData: PropTypes.object.isRequired,
  partnerSettings: PropTypes.object.isRequired,
  partnerLoading: PropTypes.bool.isRequired,
  isPartnerDomain: PropTypes.bool.isRequired,
  getSkipQuestions: PropTypes.func.isRequired,
  resetSkipQuestions: PropTypes.func.isRequired,
  counties: PropTypes.oneOfType([PropTypes.array, PropTypes.oneOf([null])]),
  countiesLoading: PropTypes.bool.isRequired,
  getCounties: PropTypes.func.isRequired,
  getCountiesByAddress: PropTypes.func.isRequired,
  cleanCountiesState: PropTypes.func.isRequired,
  getUniqueId: PropTypes.func.isRequired,
  setPartner: PropTypes.func.isRequired,
  // getPartner: PropTypes.func.isRequired,
  isIframe: PropTypes.bool.isRequired,
  iframeType: PropTypes.string.isRequired,
  appPartner: PropTypes.string.isRequired,
  internet_option: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([null])]),
  countiesZipCode: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  asyncInternetOptions: PropTypes.func.isRequired,
  handlePrefetch: PropTypes.func.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  submitForm: PropTypes.func.isRequired,
};

const mapStateToProps = ({ app, partner, wizard, counties, channels }) => ({
  isIframe: app.isIframe,
  iframeType: app.iframeType,
  appPartner: app.partner,
  partner: wizard.partner,
  partner_id: wizard.partner_id,
  unique_id: wizard.unique_id,
  skipQuestions: wizard.skipQuestions,
  extraQuestions: wizard.extraQuestions || [],
  requiredQuestions: wizard.requiredQuestions,
  skipQuestionsLoading: wizard.loading,
  partnerData: partner.data,
  partnerSettings: partner.data?.settings || {},
  partnerLoading: partner.loadings.partner,
  isPartnerDomain: partner.isPartnerDomain,
  counties: counties.counties,
  internet_option: counties.internet_option,
  countiesZipCode: counties.zip_code,
  countiesLoading: counties.loading,
  submitLoading: channels.loadings.submitForm,
});

const mapDispatchToProps = dispatch => {
  return {
    getSkipQuestions: (slug, params) =>
      dispatch(WizardActions.getSkipQuestions.action({ slug, params })),
    resetSkipQuestions: () => dispatch(WizardActions.resetSkipQuestions.action()),
    getUniqueId: (slug, form_version) => {
      dispatch(WizardActions.getUniqueId.action({ slug, form_version }));
    },
    getCounties: (zipcode, partner_id_fk, unique_id, location) => {
      dispatch(CountiesActions.getCounties.action({ zipcode, partner_id_fk, unique_id, location }));
    },
    getCountiesByAddress: (address, unique_id, zipcode) => {
      dispatch(CountiesActions.getCountiesByAddress.action({ address, unique_id, zipcode }));
    },
    cleanCountiesState: () => dispatch(CountiesActions.cleanCountiesState.action()),
    setPartner: partner => dispatch(AppActions.setPartner.action({ partner })),
    asyncInternetOptions: unique_id => {
      dispatch(WizardActions.asyncInternetOptions.action({ unique_id }));
    },
    submitForm: (data, callback) => dispatch(ChannelsActions.submitForm.action({ data, callback })),
  };
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withTranslation(),
  withPrefetch(),
  withRouter,
)(StartContent);
